import * as Popover from "@radix-ui/react-popover";
import {
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
    forwardRef,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import { flushSync } from "react-dom";
import { cx } from "../../utils/cx";
import Icon from "../../../base/components/Icon";
import { TimeColumn } from "./TimeColumn";
import styles from "./TimePicker.module.scss";

const ClockIcon = () => <Icon icon="clock" />;

type Period = "AM" | "PM";
type Segment = "hour" | "minute" | "period";

type TimeParts = {
    hour12: number;
    minute: number;
    period: Period;
};

/** Draft may be incomplete while the user is typing (native-like `--`). */
type DraftParts = {
    hour12: number | null;
    minute: number | null;
    period: Period | null;
};

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const PERIODS: Period[] = ["AM", "PM"];

const pad2 = (n: number) => String(n).padStart(2, "0");

const EMPTY_DRAFT: DraftParts = {
    hour12: null,
    minute: null,
    period: null,
};

const isComplete = (d: DraftParts): boolean =>
    d.hour12 != null && d.minute != null && d.period != null;

const toTimeParts = (d: DraftParts): TimeParts | null =>
    d.hour12 != null && d.minute != null && d.period != null
        ? { hour12: d.hour12, minute: d.minute, period: d.period }
        : null;

const toDraft = (parts: TimeParts | null): DraftParts =>
    parts
        ? {
              hour12: parts.hour12,
              minute: parts.minute,
              period: parts.period,
          }
        : { ...EMPTY_DRAFT };

/** Parse `HH:mm` or `HH:mm:ss` (24h) into 12h parts. */
export function parseTimeValue(value?: string | null): TimeParts | null {
    if (!value || !/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) return null;
    const [hRaw, mRaw] = value.split(":").map(Number);
    if (
        Number.isNaN(hRaw) ||
        Number.isNaN(mRaw) ||
        hRaw < 0 ||
        hRaw > 23 ||
        mRaw < 0 ||
        mRaw > 59
    ) {
        return null;
    }
    const period: Period = hRaw >= 12 ? "PM" : "AM";
    const hour12 = hRaw % 12 === 0 ? 12 : hRaw % 12;
    return { hour12, minute: mRaw, period };
}

/** Format 12h parts to `HH:mm` (24h). */
export function formatTimeValue(parts: TimeParts): string {
    let hour24 = parts.hour12 % 12;
    if (parts.period === "PM") hour24 += 12;
    return `${pad2(hour24)}:${pad2(parts.minute)}`;
}

export interface TimePickerProps {
    /** Floating label. */
    label?: string;
    /**
     * Controlled value in 24h `HH:mm` (same as native `input type="time"`).
     * Pass `""` / `null` / omit for empty.
     */
    value?: string | null;
    /** Uncontrolled initial value (`HH:mm`). */
    defaultValue?: string;
    /** Fires with 24h `HH:mm` when the user commits a complete time (or `""` when cleared). */
    onChange?: (value: string) => void;
    /** Fires when the popover opens or closes. */
    onOpenChange?: (open: boolean) => void;
    helperText?: string;
    error?: boolean | string;
    disabled?: boolean;
    required?: boolean;
    fullWidth?: boolean;
    className?: string;
    name?: string;
    id?: string;
    /** Minute step (default 1). Use 5 / 15 for denser lists. */
    minuteStep?: number;
}

/**
 * Treez DS — TimePicker.
 *
 * Editable segmented field (hour / minute / AM·PM) that mirrors native
 * `input type="time"` keyboard behaviour, with DS chrome. The column
 * popover opens only from the clock icon.
 */
export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
    function TimePicker(
        {
            label = "Time",
            value,
            defaultValue,
            onChange,
            onOpenChange,
            helperText,
            error,
            disabled = false,
            required,
            fullWidth = false,
            className,
            name,
            id,
            minuteStep = 1,
        },
        ref,
    ) {
        const reactId = useId();
        const fieldId = id ?? `ds-timepicker-${reactId}`;
        const messageId = `${fieldId}-msg`;
        const labelId = `${fieldId}-lbl`;

        const isControlled = onChange !== undefined;
        const [inner, setInner] = useState(defaultValue ?? "");
        const selectedValue = isControlled ? value ?? "" : inner;
        const parsed = parseTimeValue(selectedValue);

        const hasError = Boolean(error);
        const message = typeof error === "string" ? error : helperText;

        const [open, setOpen] = useState(false);
        const [focused, setFocused] = useState(false);
        const [activeSegment, setActiveSegment] = useState<Segment | null>(
            null,
        );
        const [draft, setDraft] = useState<DraftParts>(() => toDraft(parsed));
        const [popoverDraft, setPopoverDraft] = useState<TimeParts>(
            parsed ?? { hour12: 12, minute: 0, period: "AM" },
        );

        const hourRef = useRef<HTMLSpanElement | null>(null);
        const minuteRef = useRef<HTMLSpanElement | null>(null);
        const periodRef = useRef<HTMLSpanElement | null>(null);
        const fieldRef = useRef<HTMLDivElement | null>(null);
        const hourColumnRef = useRef<HTMLDivElement | null>(null);
        const minuteColumnRef = useRef<HTMLDivElement | null>(null);
        const periodColumnRef = useRef<HTMLDivElement | null>(null);
        /** Column the user last focused — drives which segment regains focus. */
        const activeColumnRef = useRef<Segment>("hour");
        /** Closing by clicking elsewhere must not pull focus back to the field. */
        const closedFromOutsideRef = useRef(false);
        const digitBufferRef = useRef("");
        const skipBlurCloseRef = useRef(false);
        /** Sync draft from external value unless the user is mid-edit. */
        const editingRef = useRef(false);
        const [fieldWidth, setFieldWidth] = useState<number | undefined>();

        const minuteOptions = useMemo(() => {
            const step = Math.max(1, Math.min(30, minuteStep));
            return MINUTES.filter(m => m % step === 0);
        }, [minuteStep]);

        const hourLabels = useMemo(() => HOURS.map(pad2), []);
        const minuteLabels = useMemo(
            () => minuteOptions.map(pad2),
            [minuteOptions],
        );

        const hasValue = isComplete(draft);
        /** Floated label + visible segment values (idle empty only shows the label). */
        const showValues = hasValue || focused || open;

        useEffect(() => {
            if (editingRef.current) return;
            setDraft(toDraft(parseTimeValue(selectedValue)));
        }, [selectedValue]);

        const segmentTabIndex = (segment: Segment) => {
            if (disabled) return -1;
            // Single tab stop into the group (native time input behaviour).
            if (activeSegment == null) return segment === "hour" ? 0 : -1;
            return activeSegment === segment ? 0 : -1;
        };

        const commit = (next: DraftParts) => {
            const complete = toTimeParts(next);
            if (complete) {
                const formatted = formatTimeValue(complete);
                if (!isControlled) setInner(formatted);
                onChange?.(formatted);
                return;
            }
            if (
                next.hour12 == null &&
                next.minute == null &&
                next.period == null
            ) {
                if (!isControlled) setInner("");
                onChange?.("");
            }
        };

        const applyDraft = (next: DraftParts) => {
            setDraft(next);
            commit(next);
        };

        const focusSegment = (segment: Segment) => {
            digitBufferRef.current = "";
            flushSync(() => {
                setActiveSegment(segment);
            });
            const node =
                segment === "hour"
                    ? hourRef.current
                    : segment === "minute"
                    ? minuteRef.current
                    : periodRef.current;
            node?.focus();
        };

        const moveSegment = (from: Segment, dir: -1 | 1) => {
            const order: Segment[] = ["hour", "minute", "period"];
            const idx = order.indexOf(from) + dir;
            if (idx < 0 || idx >= order.length) return;
            focusSegment(order[idx]);
        };

        const bumpHour = (current: number | null, dir: 1 | -1) => {
            const base = current ?? 12;
            return ((((base - 1 + dir) % 12) + 12) % 12) + 1;
        };

        const bumpMinute = (current: number | null, dir: 1 | -1) => {
            const step = Math.max(1, Math.min(30, minuteStep));
            const base = current ?? 0;
            return (((base + dir * step) % 60) + 60) % 60;
        };

        const bumpPeriod = (current: Period | null): Period =>
            current === "PM" ? "AM" : "PM";

        const onSegmentKeyDown = (
            segment: Segment,
            e: KeyboardEvent<HTMLSpanElement>,
        ) => {
            if (disabled) return;

            if (e.key === "ArrowLeft") {
                e.preventDefault();
                moveSegment(segment, -1);
                return;
            }
            if (e.key === "ArrowRight") {
                e.preventDefault();
                moveSegment(segment, 1);
                return;
            }
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                e.preventDefault();
                const dir = e.key === "ArrowUp" ? 1 : -1;
                digitBufferRef.current = "";
                if (segment === "hour") {
                    applyDraft({
                        ...draft,
                        hour12: bumpHour(draft.hour12, dir),
                    });
                } else if (segment === "minute") {
                    applyDraft({
                        ...draft,
                        minute: bumpMinute(draft.minute, dir),
                    });
                } else {
                    applyDraft({
                        ...draft,
                        period: bumpPeriod(draft.period),
                    });
                }
                return;
            }

            if (e.key === "Backspace" || e.key === "Delete") {
                e.preventDefault();
                digitBufferRef.current = "";
                if (segment === "hour") {
                    applyDraft({ ...draft, hour12: null });
                } else if (segment === "minute") {
                    applyDraft({ ...draft, minute: null });
                } else {
                    applyDraft({ ...draft, period: null });
                }
                return;
            }

            if (e.key === "Tab") {
                digitBufferRef.current = "";
                if (!e.shiftKey && segment !== "period") {
                    e.preventDefault();
                    moveSegment(segment, 1);
                } else if (e.shiftKey && segment !== "hour") {
                    e.preventDefault();
                    moveSegment(segment, -1);
                }
                return;
            }

            if (segment === "period") {
                const upper = e.key.toUpperCase();
                if (upper === "A") {
                    e.preventDefault();
                    applyDraft({ ...draft, period: "AM" });
                    return;
                }
                if (upper === "P") {
                    e.preventDefault();
                    applyDraft({ ...draft, period: "PM" });
                    return;
                }
                return;
            }

            if (!/^\d$/.test(e.key)) return;
            e.preventDefault();

            const digit = e.key;
            const buffer = `${digitBufferRef.current}${digit}`.slice(-2);
            digitBufferRef.current = buffer;

            if (segment === "hour") {
                const n = Number(buffer);
                if (buffer.length === 1) {
                    if (n === 0) {
                        // Wait for a second digit (01–09).
                        return;
                    }
                    if (n >= 2 && n <= 9) {
                        applyDraft({ ...draft, hour12: n });
                        digitBufferRef.current = "";
                        focusSegment("minute");
                        return;
                    }
                    // n === 1 → wait for second digit (10–12)
                    applyDraft({ ...draft, hour12: n });
                    return;
                }
                // Two digits
                let hour = n;
                if (hour === 0) hour = 12;
                if (hour > 12) hour = Number(digit) || 12;
                applyDraft({ ...draft, hour12: hour });
                digitBufferRef.current = "";
                focusSegment("minute");
                return;
            }

            // minutes
            const n = Number(buffer);
            if (buffer.length === 1) {
                if (n >= 6) {
                    applyDraft({ ...draft, minute: n });
                    digitBufferRef.current = "";
                    focusSegment("period");
                    return;
                }
                applyDraft({ ...draft, minute: n });
                return;
            }
            applyDraft({ ...draft, minute: Math.min(59, n) });
            digitBufferRef.current = "";
            focusSegment("period");
        };

        const onFieldMouseDown = (e: MouseEvent<HTMLDivElement>) => {
            if (disabled) return;
            const target = e.target as HTMLElement;
            if (target.closest("[data-timepicker-clock]")) return;
            if (target.closest("[data-timepicker-segment]")) return;
            e.preventDefault();
            editingRef.current = true;
            focusSegment(
                draft.hour12 == null
                    ? "hour"
                    : draft.minute == null
                    ? "minute"
                    : draft.period == null
                    ? "period"
                    : "hour",
            );
        };

        const onSegmentFocus = (segment: Segment) => {
            editingRef.current = true;
            setFocused(true);
            setActiveSegment(segment);
            digitBufferRef.current = "";
        };

        const onSegmentBlur = (e: FocusEvent<HTMLSpanElement>) => {
            const next = e.relatedTarget as Node | null;
            const root = e.currentTarget.closest("[data-ui='timepicker']");
            if (skipBlurCloseRef.current) return;
            if (root && next && root.contains(next)) return;
            // Left the field entirely
            window.setTimeout(() => {
                if (skipBlurCloseRef.current) return;
                const active = document.activeElement;
                const stillInside =
                    root && active instanceof Node && root.contains(active);
                if (stillInside) return;
                editingRef.current = false;
                setFocused(false);
                setActiveSegment(null);
                digitBufferRef.current = "";
                // Re-sync from committed value if draft was left incomplete
                setDraft(toDraft(parseTimeValue(selectedValue)));
            }, 0);
        };

        const setOpenSafe = (next: boolean) => {
            if (next) {
                /*
                 * Seed the flyout in the same batch as `open`. Doing it in an
                 * effect would land too late: child effects run before the
                 * parent's, so the columns would anchor on the previous value.
                 */
                setPopoverDraft(
                    parseTimeValue(selectedValue) ??
                        toTimeParts(draft) ?? {
                            hour12: 12,
                            minute: 0,
                            period: "AM",
                        },
                );
                setFieldWidth(fieldRef.current?.offsetWidth);
                activeColumnRef.current = "hour";
            }
            setOpen(next);
            onOpenChange?.(next);
            if (!next) {
                editingRef.current = false;
            }
        };

        const pick = (patch: Partial<TimeParts>) => {
            const next = { ...popoverDraft, ...patch };
            setPopoverDraft(next);
            editingRef.current = false;
            setDraft(next);
            commit(next);
        };

        const focusColumn = (segment: Segment) => {
            activeColumnRef.current = segment;
            const node =
                segment === "hour"
                    ? hourColumnRef.current
                    : segment === "minute"
                    ? minuteColumnRef.current
                    : periodColumnRef.current;
            node?.focus();
        };

        const navigateColumn = (from: Segment, dir: -1 | 1) => {
            const order: Segment[] = ["hour", "minute", "period"];
            const idx = order.indexOf(from) + dir;
            focusColumn(
                order[((idx % order.length) + order.length) % order.length],
            );
        };

        const hourIndex = popoverDraft.hour12 - 1;
        const minuteIndex = minuteOptions.indexOf(popoverDraft.minute);
        const periodIndex = PERIODS.indexOf(popoverDraft.period);
        /** Looping only pays off when the cycle is taller than the viewport. */
        const canLoop = (length: number) => length > 8;

        const hourText = draft.hour12 != null ? pad2(draft.hour12) : "--";
        const minuteText = draft.minute != null ? pad2(draft.minute) : "--";
        const periodText = draft.period != null ? draft.period : "--";

        return (
            <div
                className={cx(
                    styles.root,
                    fullWidth && styles.fullWidth,
                    className,
                )}
                data-disabled={disabled || undefined}
                data-error={hasError || undefined}
                data-focused={focused || undefined}
                data-has-value={hasValue || undefined}
                data-open={open || undefined}
                data-ui="timepicker"
            >
                {name ? (
                    <input
                        aria-hidden
                        name={name}
                        readOnly
                        tabIndex={-1}
                        type="hidden"
                        value={(() => {
                            const complete = toTimeParts(draft);
                            return complete
                                ? formatTimeValue(complete)
                                : selectedValue;
                        })()}
                    />
                ) : null}

                {/* Focus bridge for external ref.focus() */}
                <input
                    aria-hidden
                    className={styles.focusBridge}
                    disabled={disabled}
                    onFocus={() => {
                        if (!disabled) focusSegment("hour");
                    }}
                    ref={ref}
                    tabIndex={-1}
                    type="text"
                />

                <Popover.Root
                    onOpenChange={next => {
                        setOpenSafe(next);
                    }}
                    open={open}
                >
                    <Popover.Anchor asChild>
                        <div
                            className={styles.field}
                            onMouseDown={onFieldMouseDown}
                            ref={fieldRef}
                        >
                            <span className={styles.inner}>
                                <span
                                    aria-describedby={
                                        message ? messageId : undefined
                                    }
                                    aria-labelledby={labelId}
                                    className={cx(
                                        styles.segments,
                                        !showValues && styles.segmentsIdle,
                                    )}
                                    role="group"
                                >
                                    <span
                                        aria-label="Hours"
                                        aria-valuemax={12}
                                        aria-valuemin={1}
                                        aria-valuenow={
                                            draft.hour12 ?? undefined
                                        }
                                        aria-valuetext={
                                            draft.hour12 == null
                                                ? "blank"
                                                : hourText
                                        }
                                        className={cx(
                                            styles.segment,
                                            activeSegment === "hour" &&
                                                styles.segmentActive,
                                        )}
                                        data-timepicker-segment="hour"
                                        inputMode="numeric"
                                        onBlur={onSegmentBlur}
                                        onFocus={() => onSegmentFocus("hour")}
                                        onKeyDown={e =>
                                            onSegmentKeyDown("hour", e)
                                        }
                                        ref={hourRef}
                                        role="spinbutton"
                                        tabIndex={segmentTabIndex("hour")}
                                    >
                                        {hourText}
                                    </span>
                                    <span
                                        aria-hidden
                                        className={styles.separator}
                                    >
                                        :
                                    </span>
                                    <span
                                        aria-label="Minutes"
                                        aria-valuemax={59}
                                        aria-valuemin={0}
                                        aria-valuenow={
                                            draft.minute ?? undefined
                                        }
                                        aria-valuetext={
                                            draft.minute == null
                                                ? "blank"
                                                : minuteText
                                        }
                                        className={cx(
                                            styles.segment,
                                            activeSegment === "minute" &&
                                                styles.segmentActive,
                                        )}
                                        data-timepicker-segment="minute"
                                        inputMode="numeric"
                                        onBlur={onSegmentBlur}
                                        onFocus={() => onSegmentFocus("minute")}
                                        onKeyDown={e =>
                                            onSegmentKeyDown("minute", e)
                                        }
                                        ref={minuteRef}
                                        role="spinbutton"
                                        tabIndex={segmentTabIndex("minute")}
                                    >
                                        {minuteText}
                                    </span>
                                    <span
                                        aria-hidden
                                        className={styles.separator}
                                    >
                                        {" "}
                                    </span>
                                    <span
                                        aria-label="AM/PM"
                                        aria-valuetext={
                                            draft.period == null
                                                ? "blank"
                                                : periodText
                                        }
                                        className={cx(
                                            styles.segment,
                                            styles.segmentPeriod,
                                            activeSegment === "period" &&
                                                styles.segmentActive,
                                        )}
                                        data-timepicker-segment="period"
                                        onBlur={onSegmentBlur}
                                        onFocus={() => onSegmentFocus("period")}
                                        onKeyDown={e =>
                                            onSegmentKeyDown("period", e)
                                        }
                                        ref={periodRef}
                                        role="spinbutton"
                                        tabIndex={segmentTabIndex("period")}
                                    >
                                        {periodText}
                                    </span>
                                </span>
                                <span
                                    className={styles.floatingLabel}
                                    id={labelId}
                                >
                                    {label}
                                    {required ? " *" : ""}
                                </span>
                            </span>

                            <Popover.Trigger asChild>
                                <button
                                    aria-label={`Open ${label} picker`}
                                    className={styles.iconButton}
                                    data-timepicker-clock
                                    disabled={disabled}
                                    onMouseDown={() => {
                                        skipBlurCloseRef.current = true;
                                    }}
                                    onMouseUp={() => {
                                        skipBlurCloseRef.current = false;
                                    }}
                                    type="button"
                                >
                                    <ClockIcon />
                                </button>
                            </Popover.Trigger>
                        </div>
                    </Popover.Anchor>

                    <Popover.Portal>
                        <Popover.Content
                            align="start"
                            className={styles.flyout}
                            collisionPadding={16}
                            onCloseAutoFocus={e => {
                                e.preventDefault();
                                if (closedFromOutsideRef.current) {
                                    closedFromOutsideRef.current = false;
                                    return;
                                }
                                focusSegment(activeColumnRef.current);
                            }}
                            onInteractOutside={() => {
                                closedFromOutsideRef.current = true;
                            }}
                            onOpenAutoFocus={e => {
                                e.preventDefault();
                                window.requestAnimationFrame(() =>
                                    focusColumn("hour"),
                                );
                            }}
                            side="bottom"
                            sideOffset={4}
                            style={
                                fieldWidth
                                    ? {
                                          width: fieldWidth,
                                          minWidth: fieldWidth,
                                      }
                                    : undefined
                            }
                        >
                            <TimeColumn
                                ariaLabel="Hours"
                                columnRef={hourColumnRef}
                                idBase={`${fieldId}-hour`}
                                loop={canLoop(hourLabels.length)}
                                onColumnNav={dir => navigateColumn("hour", dir)}
                                onConfirm={() => setOpenSafe(false)}
                                onFocusColumn={() => {
                                    activeColumnRef.current = "hour";
                                }}
                                onSelectIndex={index =>
                                    pick({ hour12: HOURS[index] })
                                }
                                options={hourLabels}
                                selectedIndex={hourIndex}
                            />

                            <TimeColumn
                                ariaLabel="Minutes"
                                columnRef={minuteColumnRef}
                                idBase={`${fieldId}-minute`}
                                loop={canLoop(minuteLabels.length)}
                                onColumnNav={dir =>
                                    navigateColumn("minute", dir)
                                }
                                onConfirm={() => setOpenSafe(false)}
                                onFocusColumn={() => {
                                    activeColumnRef.current = "minute";
                                }}
                                onSelectIndex={index =>
                                    pick({ minute: minuteOptions[index] })
                                }
                                options={minuteLabels}
                                selectedIndex={minuteIndex}
                            />

                            <TimeColumn
                                ariaLabel="Period"
                                className={styles.columnPeriod}
                                columnRef={periodColumnRef}
                                idBase={`${fieldId}-period`}
                                loop={false}
                                onColumnNav={dir =>
                                    navigateColumn("period", dir)
                                }
                                onConfirm={() => setOpenSafe(false)}
                                onFocusColumn={() => {
                                    activeColumnRef.current = "period";
                                }}
                                onSelectIndex={index =>
                                    pick({ period: PERIODS[index] })
                                }
                                options={PERIODS}
                                selectedIndex={periodIndex}
                            />
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>

                {message ? (
                    <span className={styles.helper} id={messageId}>
                        {message}
                    </span>
                ) : null}
            </div>
        );
    },
);

TimePicker.displayName = "TimePicker";
